/* TAPS how-it-works kinetic engine — Direction A (same kinetic language as the quiz page).
   Hero: word mask-rise headline + rise staggers + HUD boot-flicker + segmented meter fill.
   Scroll: stepper connector fills step-by-step (nodes light up, cards punch in),
   mission-log punch-in, achievement tiles stagger + unlock-chip stamp, FAQ/finale rises.
   Accordions + cohort countdown run GSAP-free. Gate: .hwk-js primed the hidden states
   pre-paint (inline sync script in the section); GSAP releases them, 1.9s bail if absent.
   Exposed as window.HWK ({ init }). */
(function () {
  "use strict";

  var reduced = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- countdown to the cohort weekend ---------- */
  function countdown(root) {
    var el = root.querySelector("[data-hwk-cd]");
    if (!el) return;
    var val = el.querySelector("[data-hwk-cd-val]");
    var target = new Date(el.getAttribute("data-cohort-iso")).getTime();
    if (!val || isNaN(target)) return;
    var DUR = 48 * 3600000; /* the live weekend window */
    function tick() {
      var diff = target - Date.now();
      if (diff > 0) {
        var d = Math.floor(diff / 86400000),
          h = Math.floor((diff % 86400000) / 3600000),
          m = Math.floor((diff % 3600000) / 60000);
        val.textContent = d >= 1 ? d + "d " + h + "h" : (h >= 1 ? h + "h " + m + "m" : m + "m");
      } else if (-diff <= DUR) {
        val.textContent = "Live now";
      } else {
        val.textContent = "Next cohort soon";
      }
    }
    tick();
    setInterval(tick, 30000);
  }

  /* ---------- accordions: mission log + FAQ (green rail, + -> x morph) ---------- */
  function accordions(root) {
    root.querySelectorAll("[data-hwk-acc]").forEach(function (list) {
      list.addEventListener("click", function (e) {
        var head = e.target.closest("[data-hwk-head]");
        if (!head || !list.contains(head)) return;
        var row = head.closest("[data-hwk-row]");
        if (!row) return;
        var body = row.querySelector("[data-hwk-body]");
        var open = row.classList.toggle("hwk-open");
        head.setAttribute("aria-expanded", open ? "true" : "false");
        if (body) body.style.maxHeight = open ? body.scrollHeight + "px" : "0px";
      });
    });
  }

  /* ---------- headline word splitter (mask-rise) ---------- */
  function splitWords(el) {
    var walk = function (node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      kids.forEach(function (n) {
        if (n.nodeType === 3) {
          var frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement("span");
            w.className = "hwk-w";
            var i = document.createElement("i");
            i.textContent = part;
            w.appendChild(i);
            frag.appendChild(w);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1) walk(n);
      });
    };
    walk(el);
  }

  /* ---------- brand entrance + scroll choreography ---------- */
  function entrance(root) {
    var g = window.gsap;
    if (!g || reduced) { root.classList.remove("hwk-js"); return; }
    var ST = window.ScrollTrigger;
    if (ST) { try { g.registerPlugin(ST); } catch (e) { ST = null; } }

    /* hero */
    var h1 = root.querySelector("[data-hwk-h1]");
    if (h1 && !h1.__hwkSplit) { h1.__hwkSplit = true; splitWords(h1); }
    var words = root.querySelectorAll(".hwk-w i");

    var tl = g.timeline({ defaults: { ease: "power4.out" } });
    var eb = root.querySelector(".hwk-hero-eyebrow");
    if (eb) tl.to(eb, { opacity: 1, y: 0, duration: 0.6 }, 0.05);
    if (words.length) tl.to(words, { y: 0, duration: 0.85, stagger: 0.05 }, 0.12);
    var sub = root.querySelector(".hwk-hero-sub");
    if (sub) tl.to(sub, { opacity: 1, y: 0, duration: 0.7 }, 0.42);
    var cta = root.querySelector(".hwk-hero-cta");
    if (cta) tl.to(cta, { opacity: 1, y: 0, duration: 0.7 }, 0.54);
    var cells = root.querySelectorAll(".hwk-cell");
    if (cells.length) tl.to(cells, { opacity: 1, y: 0, duration: 0.6, stagger: 0.09 }, 0.66);

    /* mono HUD boot-flicker */
    var boots = root.querySelectorAll(".hwk-cell .hwk-mono");
    if (boots.length) {
      tl.to(boots, {
        keyframes: [
          { opacity: 1, duration: 0.07 },
          { opacity: 0.25, duration: 0.06 },
          { opacity: 1, duration: 0.05 },
          { opacity: 0.5, duration: 0.05 },
          { opacity: 1, duration: 0.1 }
        ],
        stagger: 0.22
      }, 0.82);
    }
    var scan = root.querySelector(".hwk-scan");
    if (scan) tl.to(scan, { scaleX: 1, duration: 0.8, ease: "power3.out" }, 0.95);

    /* no ScrollTrigger -> release everything below the fold immediately */
    var risers = root.querySelectorAll("[data-hwk-rise],[data-hwk-punch]");
    var stepsEls = root.querySelectorAll(".hwk-step");
    var fill = root.querySelector("[data-hwk-fill]");
    var chip = root.querySelector("[data-hwk-chip]");
    if (!ST) {
      risers.forEach(function (el) { el.classList.add("hwk-in"); });
      g.set(risers, { opacity: 1, clearProps: "transform" });
      g.set(stepsEls, { opacity: 1, y: 0, scale: 1 });
      if (fill) g.set(fill, { clearProps: "width,height" });
      if (chip) g.set(chip, { opacity: 1, scale: 1, rotation: 0 });
      root.querySelectorAll(".hwk-node").forEach(function (n) { n.classList.add("hwk-lit"); });
      return;
    }

    /* generic rises / punch-ins */
    function reveal(el) {
      if (el.__hwkShown) return;
      el.__hwkShown = true;
      var punch = el.hasAttribute("data-hwk-punch");
      var delay = parseFloat(el.getAttribute("data-hwk-delay")) || 0;
      /* hand back to CSS after the tween so :hover transforms (tiles) work again */
      var release = function () {
        el.classList.add("hwk-in");
        g.set(el, { clearProps: "transform" });
      };
      if (punch) {
        g.to(el, { opacity: 1, y: 0, scale: 1, duration: 0.75, delay: delay, ease: "back.out(1.7)", onComplete: release });
      } else {
        g.to(el, { opacity: 1, y: 0, duration: 0.7, delay: delay, ease: "power4.out", onComplete: release });
      }
    }
    risers.forEach(function (el) {
      ST.create({ trigger: el, start: "top 88%", once: true, onEnter: function () { reveal(el); } });
      if (el.getBoundingClientRect().top < window.innerHeight * 0.88) reveal(el);
    });

    /* stepper: cards punch in, nodes light, segmented connector fills step-by-step */
    var stepper = root.querySelector("[data-hwk-stepper]");
    if (stepper && stepsEls.length) {
      var played = false;
      var playSteps = function () {
        if (played) return;
        played = true;
        var vertical = matchMedia("(max-width:900px)").matches;
        var stl = g.timeline();
        stepsEls.forEach(function (card, i) {
          var at = i * 0.22;
          stl.to(card, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: "back.out(1.6)" }, at);
          stl.add(function () {
            var n = card.querySelector(".hwk-node");
            if (n) n.classList.add("hwk-lit");
          }, at + 0.18);
          if (fill) {
            var pct = (((i + 1) / stepsEls.length) * 100) + "%";
            stl.to(fill, vertical ? { height: pct, duration: 0.45, ease: "power3.out" } : { width: pct, duration: 0.45, ease: "power3.out" }, at + 0.18);
          }
        });
      };
      ST.create({ trigger: stepper, start: "top 80%", once: true, onEnter: playSteps });
      if (stepper.getBoundingClientRect().top < window.innerHeight * 0.8) playSteps();
    }

    /* unlock chip stamps in once, after the tiles */
    if (chip) {
      var stamped = false;
      var stamp = function () {
        if (stamped) return;
        stamped = true;
        g.to(chip, { opacity: 1, scale: 1, rotation: 0, duration: 0.6, delay: 0.55, ease: "back.out(2)" });
      };
      ST.create({ trigger: chip, start: "top 92%", once: true, onEnter: stamp });
      if (chip.getBoundingClientRect().top < window.innerHeight * 0.92) stamp();
    }
  }

  function boot(root) {
    if (!root || root.__hwk) return;
    root.__hwk = true;
    countdown(root);
    accordions(root);
    entrance(root);
  }

  function init() {
    document.querySelectorAll("[data-hwk-root]").forEach(boot);
  }

  window.HWK = { init: boot };

  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
})();
